import { QueryClient } from '@tanstack/react-query'

/**
 * React Query Client Configuration
 * 
 * This configuration optimizes caching, retry logic, and stale time
 * based on our application's needs and API characteristics.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data is considered fresh for 5 minutes
      staleTime: 5 * 60 * 1000, // 5 minutes
      
      // Data stays in cache for 10 minutes after being unused
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      
      // Retry failed requests 3 times with exponential backoff
      retry: (failureCount, error: unknown) => {
        // Don't retry on 4xx errors (client errors)
        const status = error && typeof error === 'object' && 'status' in error && typeof error.status === 'number' ? error.status : 0;
        if (status >= 400 && status < 500) {
          return false
        }
        // Retry up to 3 times for network/server errors
        return failureCount < 3
      },
      
      // Don't refetch when user focuses window (prevents unnecessary requests)
      refetchOnWindowFocus: false,
      
      // Refetch when user comes back online
      refetchOnReconnect: true,
      
      // Don't refetch when component mounts if data is fresh
      refetchOnMount: true,
    },
    mutations: {
      // Retry mutations once on failure
      retry: 1,
    },
  },
})

/**
 * Query Keys Factory
 * 
 * Centralized query key management to ensure consistency
 * and make cache invalidation easier.
 */
export const queryKeys = {
  // Authentication
  auth: {
    user: ['auth', 'user'] as const,
    profile: ['auth', 'profile'] as const,
  },
  
  // Strategies
  strategies: {
    all: ['strategies'] as const,
    lists: () => [...queryKeys.strategies.all, 'list'] as const,
    list: (filters: Record<string, any>) => [...queryKeys.strategies.lists(), filters] as const,
    details: () => [...queryKeys.strategies.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.strategies.details(), id] as const,
    templates: ['strategies', 'templates'] as const,
    marketplace: ['strategies', 'marketplace'] as const,
  },
  
  // Backtests
  backtests: {
    all: ['backtests'] as const,
    lists: () => [...queryKeys.backtests.all, 'list'] as const,
    list: (strategyId?: string, filters?: Record<string, any>) => 
      [...queryKeys.backtests.lists(), { strategyId, ...filters }] as const,
    details: () => [...queryKeys.backtests.all, 'detail'] as const,
    detail: (id: string) => [...queryKeys.backtests.details(), id] as const,
    results: (id: string) => [...queryKeys.backtests.detail(id), 'results'] as const,
  },
  
  // Trading
  trading: {
    all: ['trading'] as const,
    symbols: ['trading', 'symbols'] as const,
    prices: (symbol: string) => ['trading', 'prices', symbol] as const,
    alerts: ['trading', 'alerts'] as const,
  },
  
  // User data
  user: {
    all: ['user'] as const,
    profile: ['user', 'profile'] as const,
    settings: ['user', 'settings'] as const,
    limits: ['user', 'limits'] as const,
    progress: ['user', 'progress'] as const,
  },
} as const

/**
 * Error handling for React Query
 */
export const handleQueryError = (error: unknown) => {
  
  // Could integrate with error reporting service here
  // e.g., Sentry, LogRocket, etc.
}

/**
 * Global error handler for mutations
 */
export const handleMutationError = (error: unknown) => {
  
  // Show user-friendly error message
  // This will integrate with our existing error handling from Phase 1.3
}
